import { useEffect } from 'react';
import type { LapRef, LapStats } from '@tla/types';
import { useWorkspaceStore } from '../store/workspaceStore';
import { lapColour, lapKey } from '../store/colours';
import { formatLapTime } from '../util/format';
import { toKmh } from '@tla/core';

/** Channels shown in km/h rather than raw m/s. */
const SPEED_CHANNELS = new Set(['speed_gps', 'speed_obd', 'speed_calc']);

/** Compact column headers for common channels; falls back to the channel name. */
const SHORT_LABELS: Record<string, string> = {
  speed_gps: 'Speed',
  speed_obd: 'Speed',
  speed_calc: 'Speed',
  rpm: 'RPM',
  throttle_pos: 'Thr',
  brake: 'Brake',
  longitudinal_acc: 'Long G',
  lateral_acc: 'Lat G',
  coolant_temp: 'Coolant',
  intake_temp: 'Intake',
};

/**
 * The value shown for a lap in a channel column: km/h for speed, the larger
 * magnitude (±) for G channels, else the raw maximum. Returns null when the lap
 * has no samples for that channel.
 */
function channelValue(lap: LapStats, channelId: string, unit: string): number | null {
  const max = lap.channelMax[channelId];
  if (max == null) return null;
  if (unit === 'G') {
    const min = lap.channelMin[channelId] ?? max;
    return Math.abs(min) > Math.abs(max) ? Math.abs(min) : Math.abs(max);
  }
  if (SPEED_CHANNELS.has(channelId)) return toKmh(max);
  return max;
}

/** Format a channel value: two decimals for G, whole numbers otherwise. */
function formatChannel(value: number, unit: string): string {
  return unit === 'G' ? value.toFixed(2) : `${Math.round(value)}`;
}

/** Format a sector time compactly: seconds under a minute, else m:ss.mmm. */
function formatSector(s: number): string {
  if (!Number.isFinite(s)) return '—';
  return s < 60 ? s.toFixed(2) : formatLapTime(s);
}

/**
 * Lap table for a session: lap time, sector times and one column per selected
 * channel (mirroring the graph channel config), with best-lap, best-sector and
 * per-channel session-best highlighting. Clicking a lap adds it to the
 * comparison (or removes it if already selected).
 */
export function LapTable(props: { sessionId: string }): React.ReactElement {
  const { sessionId } = props;
  const summary = useWorkspaceStore((s) => s.summaries[sessionId]);
  const ensureSummary = useWorkspaceStore((s) => s.ensureSummary);
  const referenceLap = useWorkspaceStore((s) => s.referenceLap);
  const comparisonLaps = useWorkspaceStore((s) => s.comparisonLaps);
  const addComparisonLap = useWorkspaceStore((s) => s.addComparisonLap);
  const removeLap = useWorkspaceStore((s) => s.removeLap);
  const channelConfig = useWorkspaceStore((s) => s.channelConfig);
  const channelMeta = useWorkspaceStore((s) => s.channelMeta);

  useEffect(() => {
    void ensureSummary(sessionId);
  }, [sessionId, ensureSummary]);

  if (!summary) return <div className="small muted" style={{ padding: 8 }}>Loading laps…</div>;

  const sectorCount = summary.bestSectorTimes.length;

  // Metric columns follow the selected (visible) graph channels, in stack order.
  const columns = [...channelConfig]
    .filter((c) => c.visible)
    .sort((a, b) => a.order - b.order)
    .map((c) => {
      const unit = channelMeta[c.channelId]?.unit ?? '';
      return {
        id: c.channelId,
        label: SHORT_LABELS[c.channelId] ?? channelMeta[c.channelId]?.name ?? c.channelId,
        unit,
      };
    });

  // Session-best (max) displayed value per channel column, for highlighting.
  const columnBest: Record<string, number> = {};
  for (const col of columns) {
    let best = -Infinity;
    for (const l of summary.laps) {
      const v = channelValue(l, col.id, col.unit);
      if (v != null && v > best) best = v;
    }
    columnBest[col.id] = best;
  }

  const active: Record<string, string> = {};
  const laps: LapRef[] = referenceLap ? [referenceLap, ...comparisonLaps] : comparisonLaps;
  laps.forEach((l, i) => {
    if (l.sessionId === sessionId) active[lapKey(l)] = lapColour(i);
  });

  return (
    <table className="laps">
      <thead>
        <tr>
          <th>Lap</th>
          <th>Time</th>
          {Array.from({ length: sectorCount }, (_, i) => (
            <th key={`s${i}`}>S{i + 1}</th>
          ))}
          {columns.map((c) => (
            <th key={c.id} title={channelMeta[c.id]?.name ?? c.id}>
              {c.label}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {summary.laps.map((lap) => {
          const ref: LapRef = { sessionId, lapNumber: lap.number };
          const colour = active[lapKey(ref)];
          const selected = colour != null;
          const isBest = lap.number === summary.bestLapNumber;
          return (
            <tr
              key={lap.number}
              className={`lap-select ${lap.valid ? '' : 'invalid'}`}
              style={colour ? { boxShadow: `inset 3px 0 0 ${colour}` } : undefined}
              onClick={() => (selected ? removeLap(ref) : void addComparisonLap(ref))}
              title={selected ? 'Remove from comparison' : 'Add to comparison'}
            >
              <td>{lap.number}</td>
              <td className={isBest ? 'best' : undefined}>{formatLapTime(lap.lapTime)}</td>
              {Array.from({ length: sectorCount }, (_, i) => {
                const t = lap.sectorTimes[i];
                const bestSector = Number.isFinite(t) && t === summary.bestSectorTimes[i];
                return (
                  <td key={`s${i}`} className={bestSector ? 'best-sector' : undefined}>
                    {formatSector(t)}
                  </td>
                );
              })}
              {columns.map((c) => {
                const v = channelValue(lap, c.id, c.unit);
                const isColBest = v != null && v === columnBest[c.id];
                return (
                  <td key={c.id} className={isColBest ? 'best' : undefined}>
                    {v != null ? formatChannel(v, c.unit) : '—'}
                  </td>
                );
              })}
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}
